import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { getCurrencySymbol } from '@angular/common';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faBolt,
  faBuildingColumns,
  faCartShopping,
  faCheck,
  faCircleInfo,
  faCreditCard,
  faFileInvoice,
  faRotateLeft,
  faShieldHalved,
  faTag,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { ITransactionEvent, TransactionEventType } from '../../../transactions-layout/models';

/**
 * Vertical event timeline sourced from TX360.
 * Renders any event type — unknown types fall back to a generic icon so
 * new backend event types display without a front-end change.
 */
@Component({
  standalone: false,
  selector: 'odin-tx-event-timeline',
  templateUrl: './tx-event-timeline.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './tx-event-timeline.component.scss',
})
export class TxEventTimelineComponent {
  /** Labels for keys the generic camelCase-to-sentence conversion gets wrong */
  private static readonly DATA_LABELS: Record<string, string> = {
    eci: 'ECI',
    avsCv2Result: 'AVS / CV2',
    authCode: 'Auth code',
    responseCode: 'Response code',
    protocolVersion: '3-D Secure version',
    ipAddress: 'IP address',
  };

  @Input({ required: true }) events: ITransactionEvent[] = [];

  public eventIcon(event: ITransactionEvent): IconProp {
    switch (event.eventType) {
      case TransactionEventType.Checkout: return faCartShopping;
      case TransactionEventType.PaymentCreated: return faCreditCard;
      case TransactionEventType.ThreeDs: return faShieldHalved;
      case TransactionEventType.Authorisation:
      case TransactionEventType.Capture:
        return event.outcome === 'failure' ? faXmark : faCheck;
      case TransactionEventType.Refund:
      case TransactionEventType.Reversal:
        return faRotateLeft;
      case TransactionEventType.Settlement: return faBuildingColumns;
      case TransactionEventType.Callback: return faBolt;
      case TransactionEventType.OrderStatus: return faTag;
      case TransactionEventType.Billing: return faFileInvoice;
      default: return faCircleInfo;
    }
  }

  public eventData(event: ITransactionEvent): { key: string; value: string }[] {
    const data = event.data;
    if (!data) return [];
    const keys = Object.keys(data).filter(
      (key) => data[key] !== null && data[key] !== undefined && data[key] !== '',
    );
    // Amount/currency arrive as two separate fields; fold them into one
    // "£131.90"-style entry instead of showing "Amount: 131.90" next to a
    // separate "Currency: GBP" chip.
    const foldCurrency = keys.includes('amount') && keys.includes('currency');

    return keys
      .filter((key) => !(foldCurrency && key === 'currency'))
      .map((key) => ({
        key: this.dataLabel(key),
        value: foldCurrency && key === 'amount'
          ? this.formatAmount(data[key], data['currency'])
          : data[key],
      }));
  }

  private formatAmount(amount: string, currencyCode: string): string {
    const symbol = getCurrencySymbol(currencyCode, 'narrow') || currencyCode;
    return `${symbol}${amount}`;
  }

  /** Event data arrives keyed as it is stored (camelCase); label it for humans. */
  private dataLabel(key: string): string {
    const known = TxEventTimelineComponent.DATA_LABELS[key];
    if (known) return known;

    const spaced = key
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
      .trim()
      .toLowerCase();
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
  }
}
